import * as React from 'react';
import styled from 'styled-components';
import { Select, type SelectOption } from '@packages/ui/composites/Select';
import { FormGrid } from '../FormGrid';
import { rem } from '../../tokens/spacing';
import type {
  CriteriaRoot,
  FieldCatalogEntry,
  ResolveOptionsContext,
  RuleBuilderConfig,
  RuleBuilderOption,
} from './types';
import {
  operatorsForField,
  resolveFieldEntry,
  updateRuleField,
  updateRuleOperator,
  updateRuleValue,
} from './criteriaTreeMutators';
import { RuleValueEditor, useRuleValueOptions } from './RuleValueEditor';
import { shouldShowRuleDelete } from './criteriaRuleBuilderLayout';

const StyledRuleRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${rem(12)};
  width: 100%;
`;

const StyledRuleFields = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
`;

const StyledRuleDelete = styled.button`
  flex: 0 0 auto;
  margin-top: ${rem(12)};
  margin-inline-start: ${rem(20)};
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
  color: var(--ui-destructive);
  line-height: 1;

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }

  .material-symbols-outlined {
    font-size: ${rem(24)};
  }
`;

export interface CriteriaRuleRowProps {
  semanticId: string;
  config: RuleBuilderConfig;
  fieldCatalog: FieldCatalogEntry[];
  criteria: CriteriaRoot;
  groupIndex: number;
  ruleIndex: number;
  readOnly: boolean;
  formValues: Record<string, unknown>;
  onCriteriaChange: (next: CriteriaRoot) => void;
  onRemoveRule: () => void;
  resolveOptions?: (
    entry: FieldCatalogEntry,
    context: ResolveOptionsContext,
  ) => Promise<RuleBuilderOption[]>;
}

export function CriteriaRuleRow({
  semanticId,
  config,
  fieldCatalog,
  criteria,
  groupIndex,
  ruleIndex,
  readOnly,
  formValues,
  onCriteriaChange,
  onRemoveRule,
  resolveOptions,
}: CriteriaRuleRowProps) {
  const rule = criteria.Conditions[groupIndex]?.SubCriteria.Conditions[ruleIndex];
  const fieldEntry = resolveFieldEntry(rule?.Field, fieldCatalog) as FieldCatalogEntry | undefined;

  const resolveContext: ResolveOptionsContext = {
    formValues,
    groupIndex,
    ruleIndex,
    criteria,
  };

  const { options: valueOptions, isLoading: valueOptionsLoading } = useRuleValueOptions({
    fieldEntry,
    context: resolveContext,
    resolveOptions,
  });

  const fieldSelectOptions: SelectOption[] = fieldCatalog.map((e) => ({
    label: e.label,
    value: e.code,
  }));

  const operatorOptions = operatorsForField(config.operators, fieldEntry).map((op) => ({
    label: op.label,
    value: op.code,
  }));

  const columns = config.matrix?.ruleRow.columns ?? 3;
  const showOperator = rule?.Field != null && String(rule.Field).trim() !== '';

  const showDelete =
    !readOnly &&
    config.toolbar?.deleteRule !== false &&
    shouldShowRuleDelete(criteria, groupIndex, ruleIndex);

  if (!rule) return null;

  return (
    <StyledRuleRow data-semantic-id={`${semanticId}Rule.${groupIndex}.${ruleIndex}`}>
      <StyledRuleFields data-semantic-id={`${semanticId}RuleFields.${groupIndex}.${ruleIndex}`}>
        <FormGrid columns={columns as 1 | 2 | 3 | 4} collapse='sm' gap={rem(20)}>
          <Select
            semanticId={`${semanticId}Field.${groupIndex}.${ruleIndex}`}
            options={fieldSelectOptions}
            value={
              rule.Field
                ? fieldSelectOptions.find((o) => o.value === rule.Field) ?? null
                : null
            }
            isDisabled={readOnly}
            placeholder='Field'
            onChange={(opt) => {
              const selected = opt as SelectOption | null;
              onCriteriaChange(
                updateRuleField(criteria, groupIndex, ruleIndex, selected?.value ?? null),
              );
            }}
          />
          {showOperator ? (
            <Select
              semanticId={`${semanticId}Operator.${groupIndex}.${ruleIndex}`}
              options={operatorOptions}
              value={
                rule.EqualityOperatorType
                  ? operatorOptions.find((o) => o.value === rule.EqualityOperatorType) ?? null
                  : null
              }
              isDisabled={readOnly}
              placeholder='Operator'
              onChange={(opt) => {
                const selected = opt as SelectOption | null;
                onCriteriaChange(
                  updateRuleOperator(
                    criteria,
                    groupIndex,
                    ruleIndex,
                    selected?.value ?? null,
                  ),
                );
              }}
            />
          ) : (
            <span aria-hidden />
          )}
          {showOperator ? (
            fieldEntry ? (
              <RuleValueEditor
                semanticId={`${semanticId}.${groupIndex}.${ruleIndex}`}
                valueKind={fieldEntry.valueKind}
                value={rule.Value}
                fieldEntry={fieldEntry}
                readOnly={readOnly}
                options={valueOptions}
                isLoadingOptions={valueOptionsLoading}
                onChange={(nextValue, displayName) => {
                  onCriteriaChange(
                    updateRuleValue(criteria, groupIndex, ruleIndex, nextValue, displayName ?? null),
                  );
                }}
              />
            ) : (
              <span aria-hidden />
            )
          ) : (
            <span aria-hidden />
          )}
        </FormGrid>
      </StyledRuleFields>
      {showDelete ? (
        <StyledRuleDelete
          type='button'
          aria-label='Delete rule'
          data-semantic-id={`${semanticId}DeleteRule.${groupIndex}.${ruleIndex}`}
          onClick={onRemoveRule}
        >
          <span className='material-symbols-outlined' aria-hidden>
            delete
          </span>
        </StyledRuleDelete>
      ) : null}
    </StyledRuleRow>
  );
}

CriteriaRuleRow.displayName = 'CriteriaRuleRow';
